import { DownOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/css';
import { useTranslate } from '@tolgee/react';
import { Table, TableColumnsType } from 'antd';
import { useIsERP } from 'hooks/business/useIsERP';
import { CopyText } from '../CopyText';
import { SkuStockStatus } from 'components/SkuStockStatus';
import { PopoverPanel } from './PopoverPanel';
import { useSize } from 'ahooks';
import PreOrder from 'views/Conversation/Sider/Item/PreOrder';

interface Rect {
  top: number;
  left: number;
  width: number;
}

interface ProductInfoSkuGridProps {
  /** 浮窗定位参考的容器，浮窗将紧贴其下方且宽度一致 */
  containerRef: React.RefObject<HTMLDivElement | null>;
  storeId: Conversation.Conversation['storeId'];
  skuInfo: Conversation.ProductSKUInfo[];
  preOrderStatus: Conversation.Product['preOrderStatus'];
}

interface GridTableProps {
  cellWidth: number;
  storeId: Conversation.Conversation['storeId'];
  skuInfo: Conversation.ProductSKUInfo[];
  preOrderStatus: Conversation.Product['preOrderStatus'];
}

const TRANSITION_MS = 200;

const GridTable = (props: GridTableProps) => {
  const { cellWidth, storeId, skuInfo, preOrderStatus } = props;
  const { t } = useTranslate();

  const isERP = useIsERP(storeId);

  const columns: TableColumnsType<Conversation.ProductSKUInfo> = useMemo(() => {
    return [
      {
        title: 'SKU',
        dataIndex: 'sku',
        width: cellWidth,
        render: (_, { sku, isPreOrder }) => {
          return (
            <div className="flex items-center">
              {isPreOrder && <PreOrder preOrderStatus={preOrderStatus} />}
              <CopyText
                text={sku}
                style={{ width: cellWidth - (isPreOrder ? 20 : 0) }}
                tooltip={sku}
              />
            </div>
          );
        },
      },
      {
        title: t('sider.SKU货号'),
        dataIndex: 'sellerSku',
        width: cellWidth,
        render: (sellerSku: string) => (
          <CopyText text={sellerSku} style={{ width: cellWidth }} tooltip={sellerSku} />
        ),
      },
      {
        title: t('outofstock_stock_status'),
        dataIndex: 'skuStockStatus',
        render: (_, record) => <SkuStockStatus skuInfo={record} isERP={isERP} />,
      },
    ];
  }, [t, isERP, cellWidth, preOrderStatus]);

  return (
    <div className="text-[12px] text-[#262626]">
      <Table
        scroll={{
          y: 140,
        }}
        className={tableCls}
        rowClassName={rowCls}
        dataSource={skuInfo}
        columns={columns}
        size="small"
        pagination={false}
      />
    </div>
  );
};

export const ProductInfoSkuGrid = (props: ProductInfoSkuGridProps) => {
  const { containerRef, storeId, skuInfo, preOrderStatus } = props;
  const { t } = useTranslate();
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  const [rect, setRect] = useState<Rect | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerSize = useSize(triggerRef);
  const triggerHeight = triggerSize?.height ?? 0;

  const updateRect = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRect({ top: r.bottom, left: r.left, width: r.width });
  }, [containerRef]);

  // 打开时测量并监听滚动/缩放以更新位置
  useLayoutEffect(() => {
    if (!visible) return;
    updateRect();
    const onUpdate = () => updateRect();
    window.addEventListener('resize', onUpdate);
    window.addEventListener('scroll', onUpdate, true);
    return () => {
      window.removeEventListener('resize', onUpdate);
      window.removeEventListener('scroll', onUpdate, true);
    };
  }, [visible, updateRect]);

  const toggle = useCallback(() => {
    if (visible) {
      setVisible(false);
      setClosing(true);
      setTimeout(() => setClosing(false), TRANSITION_MS);
    } else {
      setClosing(false);
      // 打开时同步测量，避免浮窗晚一帧出现
      const el = containerRef.current;
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.bottom, left: r.left, width: r.width });
      }
      setVisible(true);
    }
  }, [visible, containerRef]);

  // 点击 panel 和触发按钮以外的区域时关闭 panel
  useEffect(() => {
    if (!visible) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return;
      }
      setVisible(false);
      setClosing(true);
      setTimeout(() => setClosing(false), TRANSITION_MS);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [visible]);

  const cellWidth = rect?.width ? (rect.width - 28) / 3 : 100;

  return (
    <>
      <span
        ref={triggerRef}
        className="text-[12px] text-[#84868C] cursor-pointer"
        onClick={toggle}
        role="button"
        tabIndex={0}
      >
        {t('other.more')}
        <DownOutlined
          className="ml-1 text-[10px]"
          style={{
            transform: visible ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
        />
      </span>
      <PopoverPanel
        visible={visible}
        closing={closing}
        rect={rect}
        panelRef={panelRef}
        transitionMs={TRANSITION_MS}
        offsetHeight={triggerHeight}
      >
        <GridTable
          cellWidth={cellWidth}
          storeId={storeId}
          skuInfo={skuInfo}
          preOrderStatus={preOrderStatus}
        />
      </PopoverPanel>
    </>
  );
};

const tableCls = css`
  .ant-table-thead {
    .ant-table-cell {
      border: none;
      padding-top: 3px !important;
      padding-bottom: 3px !important;
      background: #f0f2f5;
      font-weight: 400;
      font-size: 12px;
      color: #515359;
    }
    .ant-table-cell::before {
      display: none;
    }
  }
`;

const rowCls = css`
  & > .ant-table-cell {
    border: none;
    padding-top: 5px !important;
    padding-bottom: 5px !important;
    font-weight: 400;
    font-size: 12px;
    color: #515359;
  }
`;
